import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Check, AlertTriangle, Download, FileText, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const DEFAULT_CHECKLIST = [
  { id: 1, category: 'Administrative Safeguards', item: 'Security Management Process', required: true, description: 'Policies and procedures to prevent, detect, contain, and correct security violations' },
  { id: 2, category: 'Administrative Safeguards', item: 'Assigned Security Responsibility', required: true, description: 'Designated security official responsible for HIPAA compliance' },
  { id: 3, category: 'Administrative Safeguards', item: 'Workforce Security', required: true, description: 'Procedures for authorizing and supervising workforce members' },
  { id: 4, category: 'Administrative Safeguards', item: 'Information Access Management', required: true, description: 'Policies for authorizing access to ePHI' },
  { id: 5, category: 'Administrative Safeguards', item: 'Security Awareness Training', required: true, description: 'Security awareness and training program for all workforce members' },
  { id: 6, category: 'Physical Safeguards', item: 'Facility Access Controls', required: true },
  { id: 7, category: 'Physical Safeguards', item: 'Workstation Use', required: true },
  { id: 8, category: 'Physical Safeguards', item: 'Workstation Security', required: true },
  { id: 9, category: 'Physical Safeguards', item: 'Device and Media Controls', required: true },
  { id: 10, category: 'Technical Safeguards', item: 'Access Control', required: true },
  { id: 11, category: 'Technical Safeguards', item: 'Audit Controls', required: true },
  { id: 12, category: 'Technical Safeguards', item: 'Integrity Controls', required: true },
  { id: 13, category: 'Technical Safeguards', item: 'Transmission Security', required: true },
  { id: 14, category: 'Privacy Rule', item: 'Notice of Privacy Practices', required: true },
  { id: 15, category: 'Privacy Rule', item: 'Client Rights', required: true },
  { id: 16, category: 'Privacy Rule', item: 'Minimum Necessary Standard', required: true },
  { id: 17, category: 'Breach Notification', item: 'Breach Notification Procedures', required: true },
  { id: 18, category: 'Business Associates', item: 'Business Associate Agreements', required: true },
];

const HIPAAComplianceChecklist = ({ token, apiCall, toast }) => {
  const [completedMap, setCompletedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadChecklist = useCallback(async () => {
    try {
      const response = await apiCall('/compliance/hipaa-checklist', 'GET', null, token);
      const saved = response?.data?.data || {};
      setCompletedMap(saved);
    } catch (error) {
      console.error('Error loading checklist:', error);
    }
    setLoading(false);
  }, [apiCall, token]);

  useEffect(() => { loadChecklist(); }, [loadChecklist]);

  const toggleItem = async (id) => {
    const newMap = { ...completedMap, [id]: !completedMap[id] };
    setCompletedMap(newMap);

    setSaving(true);
    try {
      await apiCall('/compliance/hipaa-checklist', 'PUT', { items: newMap }, token);
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast?.error('Failed to save checklist');
      setCompletedMap(completedMap);
    }
    setSaving(false);
  };

  const checklist = DEFAULT_CHECKLIST.map(item => ({
    ...item,
    completed: !!completedMap[item.id]
  }));

  const categories = [...new Set(checklist.map(item => item.category))];
  const totalItems = checklist.length;
  const completedItems = checklist.filter(item => item.completed).length;
  const compliancePercentage = ((completedItems / totalItems) * 100).toFixed(0);

  const downloadAuditReport = () => {
    const reportContent = `HIPAA COMPLIANCE AUDIT REPORT\nGenerated: ${new Date().toLocaleString()}\n\n` +
      `Overall Compliance: ${compliancePercentage}% (${completedItems}/${totalItems})\n\n` +
      categories.map(category => {
        const categoryItems = checklist.filter(item => item.category === category);
        const categoryCompleted = categoryItems.filter(item => item.completed).length;
        return `${category}\nCompliance: ${categoryCompleted}/${categoryItems.length}\n` +
          categoryItems.map(item =>
            `  [${item.completed ? 'X' : ' '}] ${item.item}`
          ).join('\n');
      }).join('\n\n');

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hipaa-compliance-audit-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast?.success('Compliance audit report downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="hipaa-compliance-checklist">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            HIPAA Compliance Status
            {saving && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-4xl font-bold text-blue-600">{compliancePercentage}%</div>
                <div className="text-sm text-gray-600">Overall Compliance</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-semibold">{completedItems}/{totalItems}</div>
                <div className="text-sm text-gray-600">Items Complete</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-600 h-4 rounded-full transition-all"
                style={{ width: `${compliancePercentage}%` }}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadAuditReport} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Download className="w-4 h-4 mr-2" /> Download Report
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('/COMPLIANCE/COMPLIANCE_PACKAGE_INDEX.md', '_blank')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <FileText className="w-4 h-4 mr-2" /> View Documentation
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {categories.map((category) => {
        const categoryItems = checklist.filter(item => item.category === category);
        const categoryCompleted = categoryItems.filter(item => item.completed).length;

        return (
          <Card key={category}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{category}</CardTitle>
                <Badge variant={categoryCompleted === categoryItems.length ? 'default' : 'secondary'}>
                  {categoryCompleted}/{categoryItems.length}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categoryItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      item.completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                    onClick={() => toggleItem(item.id)}
                    data-testid={`hipaa-item-${item.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={item.completed}
                      onChange={() => {}}
                      className="w-5 h-5 text-green-600 rounded pointer-events-none"
                    />
                    <div className="flex-1">
                      <span className={`text-sm ${item.completed ? 'text-gray-600 line-through' : 'text-gray-900 font-medium'}`}>
                        {item.item}
                      </span>
                      {item.required && <Badge variant="outline" className="ml-2 text-xs">Required</Badge>}
                      {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                    </div>
                    {item.completed ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default HIPAAComplianceChecklist;
